import api from "./api"


// =========================================================
// HELPERS
// =========================================================

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== "" &&
        value !== "all"
    )
  )


const extractFilename = (
  contentDisposition,
  fallback = "bulletin-paie.pdf"
) => {
  if (!contentDisposition) {
    return fallback
  }

  // Exemple :
  // attachment; filename="bulletin-paie-EMP-00001-PAY-00001.pdf"
  const filenameMatch =
    contentDisposition.match(
      /filename="?([^"]+)"?/i
    )

  if (
    filenameMatch &&
    filenameMatch[1]
  ) {
    return filenameMatch[1]
  }

  // Support filename*=UTF-8''
  const utf8Match =
    contentDisposition.match(
      /filename\*=UTF-8''([^;]+)/i
    )

  if (
    utf8Match &&
    utf8Match[1]
  ) {
    try {
      return decodeURIComponent(
        utf8Match[1]
      )
    } catch {
      return utf8Match[1]
    }
  }

  return fallback
}


// =========================================================
// FORM DATA
// =========================================================

export const getHrPayrollFormData = async () => {
  const response = await api.get(
    "/hr/payroll/form-data"
  )

  return response.data
}


// =========================================================
// PREVIEW
// =========================================================
//
// Sert maintenant à récupérer :
// - salaire de base
// - devise
// - heures supplémentaires de Présences
// - taux horaire
// - coefficient
// - montant automatique des heures supplémentaires
//
// =========================================================

export const getHrPayrollPreview = async (
  params = {}
) => {
  const response = await api.get(
    "/hr/payroll/preview",
    {
      params: cleanParams(params),
    }
  )

  return response.data
}


// =========================================================
// SUMMARY
// =========================================================

export const getHrPayrollSummary = async (
  params = {}
) => {
  const response = await api.get(
    "/hr/payroll/summary",
    {
      params: cleanParams(params),
    }
  )

  return response.data
}


// =========================================================
// PERIODS - LIST
// =========================================================

export const getHrPayrollPeriods = async (
  params = {}
) => {
  const response = await api.get(
    "/hr/payroll/periods",
    {
      params: cleanParams(params),
    }
  )

  return response.data
}


// =========================================================
// PERIODS - CREATE
// =========================================================

export const createHrPayrollPeriod = async (
  payload
) => {
  const response = await api.post(
    "/hr/payroll/periods",
    payload
  )

  return response.data
}


// =========================================================
// PERIODS - UPDATE
// =========================================================

export const updateHrPayrollPeriod = async (
  periodId,
  payload
) => {
  const response = await api.put(
    `/hr/payroll/periods/${periodId}`,
    payload
  )

  return response.data
}


// =========================================================
// PERIODS - DELETE
// =========================================================

export const deleteHrPayrollPeriod = async (
  periodId
) => {
  const response = await api.delete(
    `/hr/payroll/periods/${periodId}`
  )

  return response.data
}


// =========================================================
// PAYROLL - LIST
// =========================================================

export const getHrPayrolls = async (
  params = {}
) => {
  const response = await api.get(
    "/hr/payroll",
    {
      params: cleanParams(params),
    }
  )

  return response.data
}


// =========================================================
// ALIASES
// =========================================================
//
// On garde ces alias pour éviter de casser
// d'anciennes pages ou anciens appels.
//
// =========================================================

export const getPayrolls =
  getHrPayrolls

export const getHrPayroll =
  getHrPayrolls

export const getPayroll =
  getHrPayrolls

export const listPayrolls =
  getHrPayrolls


// =========================================================
// PAYROLL - CREATE
// =========================================================

export const createHrPayroll = async (
  payload
) => {
  const response = await api.post(
    "/hr/payroll",
    payload
  )

  return response.data
}


// =========================================================
// PAYROLL - UPDATE
// =========================================================

export const updateHrPayroll = async (
  payrollId,
  payload
) => {
  const response = await api.put(
    `/hr/payroll/${payrollId}`,
    payload
  )

  return response.data
}


// =========================================================
// PAYROLL - VALIDATE
// =========================================================

export const validateHrPayroll = async (
  payrollId
) => {
  const response = await api.post(
    `/hr/payroll/${payrollId}/validate`,
    {}
  )

  return response.data
}


// =========================================================
// PAYROLL - PAY
// =========================================================

export const markHrPayrollPaid = async (
  payrollId
) => {
  const response = await api.post(
    `/hr/payroll/${payrollId}/pay`,
    {}
  )

  return response.data
}


// =========================================================
// PAYROLL - PDF
// =========================================================
//
// Le backend renvoie un vrai fichier PDF.
// Ici on utilise responseType: "blob".
// Sans ça Axios essaie de lire le PDF comme du JSON.
//
// =========================================================

export const getHrPayrollPdf = async (
  payrollId
) => {
  const response = await api.get(
    `/hr/payroll/${payrollId}/pdf`,
    {
      responseType: "blob",
    }
  )

  const contentDisposition =
    response.headers?.[
      "content-disposition"
    ]

  const filename =
    extractFilename(
      contentDisposition,
      `bulletin-paie-${payrollId}.pdf`
    )

  return {
    blob: response.data,
    filename,
    contentType:
      response.headers?.[
        "content-type"
      ] || "application/pdf",
  }
}


// =========================================================
// PAYROLL - DOWNLOAD PDF
// =========================================================
//
// Cette fonction fait tout :
// 1. appelle le backend
// 2. reçoit le PDF
// 3. crée un URL temporaire
// 4. déclenche le téléchargement
// 5. libère l'URL mémoire
//
// =========================================================

export const downloadHrPayrollPdf = async (
  payrollId
) => {
  const {
    blob,
    filename,
  } =
    await getHrPayrollPdf(
      payrollId
    )

  const objectUrl =
    window.URL.createObjectURL(
      blob
    )

  const link =
    document.createElement(
      "a"
    )

  link.href =
    objectUrl

  link.download =
    filename

  document.body.appendChild(
    link
  )

  link.click()

  document.body.removeChild(
    link
  )

  window.URL.revokeObjectURL(
    objectUrl
  )

  return {
    filename,
  }
}


// =========================================================
// PAYROLL - OPEN PDF
// =========================================================
//
// Facultatif mais utile.
// Permet d'ouvrir le bulletin dans un nouvel onglet
// avant de le télécharger.
//
// =========================================================

export const openHrPayrollPdf = async (
  payrollId
) => {
  const {
    blob,
  } =
    await getHrPayrollPdf(
      payrollId
    )

  const objectUrl =
    window.URL.createObjectURL(
      blob
    )

  const newWindow =
    window.open(
      objectUrl,
      "_blank",
      "noopener,noreferrer"
    )

  // On attend un peu avant de libérer l'URL.
  // Sinon certains navigateurs peuvent perdre le document.
  setTimeout(
    () => {
      window.URL.revokeObjectURL(
        objectUrl
      )
    },
    60000
  )

  return {
    opened:
      Boolean(
        newWindow
      ),
  }
}


// =========================================================
// PAYROLL - DELETE
// =========================================================

export const deleteHrPayroll = async (
  payrollId
) => {
  const response = await api.delete(
    `/hr/payroll/${payrollId}`
  )

  return response.data
}


// =========================================================
// COMPATIBILITY ALIASES
// =========================================================
//
// On garde aussi quelques noms génériques
// si d'autres composants les utilisent plus tard.
//
// =========================================================

export const createPayroll =
  createHrPayroll

export const updatePayroll =
  updateHrPayroll

export const deletePayroll =
  deleteHrPayroll

export const validatePayroll =
  validateHrPayroll

export const payPayroll =
  markHrPayrollPaid

export const getPayrollPeriods =
  getHrPayrollPeriods

export const getPayrollPreview =
  getHrPayrollPreview

export const getPayrollSummary =
  getHrPayrollSummary

export const getPayrollPdf =
  getHrPayrollPdf

export const downloadPayrollPdf =
  downloadHrPayrollPdf

export const openPayrollPdf =
  openHrPayrollPdf